import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import {
	Box,
	Button,
	TablePagination,
	TextField,
	ThemeProvider,
	createTheme
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { isAfter } from 'date-fns';
import DataGridIcon from '../../Assets/Images/DataGrid.svg';
import { ReactComponent as TableFilterIcon } from '../../Assets/Images/TableSortIcon.svg';
import { getMobileValue } from '../../utils/util.js';
import InventoryMenu from './inventory-menu.js';
import MenuDropDown from './MenuDropdown/index.js';
import ApprovedStatusBox from './Tables/approved-status-box.js';
import WarningStatusIndicator from './Tables/warning-status-indicator.js';

const today = new Date();
const todayString = today.toISOString().split('T')[0];

const CustomNoRowsOverlay = () => (
	<div
		style={{
			display: 'flex',
			flexDirection: 'column',
			alignItems: 'center',
			justifyContent: 'center',
			height: '100%'
		}}
	>
		<img
			src={DataGridIcon}
			alt='No Data'
			style={{ height: '200px', widows: '200px' }}
		/>
		<span>No Data</span>
	</div>
);

const theme = createTheme({
	components: {
		MuiCircularProgress: {
			styleOverrides: {
				colorPrimary: {
					color: '#FFFFFF'
				}
			}
		}
	}
});

const Tables = ({
	branchTab = false,
	rows = [],
	columns = [],
	tabValue,
	newMobileNumberMap,
	hasDuplicates,
	totalRowCount,
	updatePageCount = () => {},
	showLoader = false,
	rowsPerPageRender,
	onFilterChange,
	marginX,
	pageCount = 1,
	pageSize = 10,
	columnVisibilityModel,
	kebabMenuItem = [],
	showCustomCol = true,
	tableStatus = true,
	handleSortModelChange,
	filterSearch = {},
	apiRef,
	showInfo,
	handleComment = () => {},
	isDashboard = false
}) => {
	const [anchorEl, setAnchorEl] = useState('');
	const [selectedEl, setSelectedEl] = useState('');
	const [isLoading, setIsLoading] = useState(false);
	const [columnData, setColumnData] = useState([]);
	const mapPageToNextCursor = React.useRef({});

	useEffect(() => {
		setIsLoading(showLoader);
	}, [showLoader]);

	const [paginationModel, setPaginationModel] = React.useState({
		page: pageCount - 1,
		pageSize
	});

	const open = Boolean(anchorEl);
	const handleClick = (event, params) => {
		const selectedElId = params.id;
		const item = rows.find((el) => el.id === selectedElId);
		setSelectedEl(item);
		setAnchorEl(event.currentTarget);
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	useEffect(() => {
		setPaginationModel({
			page: pageCount - 1,
			pageSize
		});
	}, [pageCount, pageSize]);

	useEffect(() => {
		if (!isLoading) {
			mapPageToNextCursor.current[paginationModel.page]
				= paginationModel.page + 1;
			updatePageCount(paginationModel.page + 1, paginationModel.pageSize);
		}
	}, [paginationModel.page, isLoading, paginationModel.pageSize]);

	const handlePaginationModelChange = (e, newPaginationModel) => {
		if (
			newPaginationModel === 0
			|| mapPageToNextCursor.current[newPaginationModel - 1]
		) {
			setPaginationModel((prev) => ({ ...prev, page: newPaginationModel }));
		}
	};

	const handleChangeRowsPerPage = (event) => {
		setPaginationModel((prev) => ({
			...prev,
			page: 0,
			pageSize: Number.parseInt(event.target.value, 10)
		}));
	};

	useEffect(() => {
		if (columns && tableStatus) {
			const index = columns.findIndex((val) => val.field === 'status');
			const temp = columns.map((val, i) => {
				if (i === index) {
					return {
						...val,
						minWidth: val.minWidth || 130,
						renderCell(params) {
							const status = params?.row?.status.toLowerCase();
							const rowData = params.row;
							if (
								['active', 'approved', 'close', 'available'].includes(status)
							) {
								return (
									<ApprovedStatusBox
										handleComment={handleComment}
										status={status}
										rowData={rowData}
										showInfo={showInfo}
										tabValue={tabValue}
									/>
								);
							}

							return (
								<WarningStatusIndicator
									tabValue={tabValue}
									handleComment={handleComment}
									rowData={rowData}
									showInfo={showInfo}
									status={status}
								/>
							);
						},
						valueGetter: (params) => params?.row?.status
					};
				}

				return { ...val };
			});
			setColumnData([...temp]);
		} else {
			setColumnData(columns);
		}
	}, [columns]);

	const customCol = {
		field: 'more',
		headerName: '',
		flex: getMobileValue(tabValue === 0, 0.08, 0.2),
		sortable: false,
		filterable: false,
		disableColumnMenu: true,
		renderCell(params) {
			if (branchTab) {
				let filterMenu = kebabMenuItem?.filter((item) => item.hasPermission);
				if (params.row.status === 'Active') {
					filterMenu = filterMenu.filter((item) => item.title !== 'Active');
				}

				if (params.row.status === 'Disable') {
					filterMenu = filterMenu.filter(
						(item) =>
							item.title !== 'Disable' && item.title !== 'Set as Default Branch'
					);
				}

				if (params.row.status === 'Approval Pending') {
					filterMenu = filterMenu.filter(
						(item) =>
							item.title !== 'Disable' && item.title !== 'Set as Default Branch'
					);
				}

				if (params.row.status === 'Rejected') {
					filterMenu = filterMenu.filter(
						(item) =>
							item.title !== 'Disable' && item.title !== 'Set as Default Branch'
					);
				}

				if (params.row.defaultBranch) {
					filterMenu = filterMenu.filter((item) => item.title === 'Edit');
				}

				return (
					<InventoryMenu
						kebabMenuItem={filterMenu}
						handleClick={(val) => {
							val.onClick(params.row);
						}}
						isBranchTab
					/>
				);
			}

			if (isDashboard) {
				const tempKebabMenuItems = kebabMenuItem?.filter(
					(item) => !item.status || item.status === params.row.status
				);

				return (
					<InventoryMenu
						kebabMenuItem={tempKebabMenuItems}
						handleClick={(val) => {
							val.onClick(params.row);
						}}
						isBranchTab
					/>
				);
			}

			return (
				<div
					style={{
						display: newCustomCol?.[0]?.field === 'more' ? 'none' : 'block'
					}}
				>
					<Button
						sx={{
							color: '#21241E',
							opacity: '0.2'
						}}
						onClick={(event) => handleClick(event, params)}
					>
						<MoreVertIcon />
					</Button>

					<MenuDropDown
						open={open}
						handleClose={handleClose}
						anchorEl={anchorEl}
						selectedItem={selectedEl}
						kebabMenuItem={kebabMenuItem}
						branchTab={branchTab}
						data={params.row}
					/>
				</div>
			);
		},
		valueGetter: (params) => params?.row
	};
	let newCustomCol = [...columnData];

	if (showCustomCol) {
		newCustomCol.push(customCol);
	}

	const temp = newCustomCol.map((item) => {
		if (item.type === 'date') {
			return {
				...item,
				type: 'date',
				filterOperators: [
					{
						label: 'Is Before or Today',
						value: 'isBeforeOrToday',
						getApplyFilterFn(filterItem) {
							if (!filterItem.value) {
								return null;
							}

							const filterDate = new Date(filterItem.value);
							return ({ value }) => {
								if (!value) {
									return false;
								}

								return !isAfter(value, filterDate);
							};
						},
						InputComponent
					}
				]
			};
		}

		return {
			...item,
			sortComparator: () => []
		};
	});

	newCustomCol = [...temp];
	return (
		<Box sx={{ width: '100%' }}>
			{hasDuplicates && (
				<div>
					<h1>Duplicate Exists</h1>
					{Object.keys(newMobileNumberMap).map((mobileNumber) => {
						const duplicateObjects = newMobileNumberMap[mobileNumber];
						if (duplicateObjects.length > 1) {
							return (
								<div key={mobileNumber}>
									<h3>Mobile Number: {mobileNumber}</h3>
									<ul>
										{duplicateObjects.map((obj, index) => (
											<li key={'a' + index}>{obj.userName}</li>
										))}
									</ul>
									Upload Again
								</div>
							);
						}

						return null;
					})}
				</div>
			)}
			{!hasDuplicates && (
				<ThemeProvider theme={theme}>
					<DataGrid
						sx={{
							mx: getMobileValue(marginX, marginX, '20px'),
							fontFamily: 'Gilroy-Medium',
							'& .MuiDataGrid-columnHeaders': {
								color: '#8E8E8E',
								fontSize: '12px'
							},

							'& .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-cell:focus-within':
								{
									outline: 'none'
								},

							'& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-cell:focus': {
								outline: 'none'
							},
							'& .MuiDataGrid-cell': {
								border: 'none'
							},
							'.MuiDataGrid-iconButtonContainer': {
								visibility: 'visible',
								stroke: '#B7D423',
								width: 'auto'
							},
							'.MuiDataGrid-columnHeader:not(.MuiDataGrid-columnHeader--sorted) .MuiDataGrid-sortIcon ':
								{
									opacity: 1,
									stroke: '#646464'
								},
							'--DataGrid-overlayHeight': '300px',
							'&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell': {
								py: '8px'
							},
							'&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell': {
								py: '15px'
							},
							'&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell': {
								py: '22px'
							},
							minHeight: !showLoader && rows.length < 5 ? '0px' : '500px'
						}}
						disableColumnResize
						apiRef={apiRef}
						getRowHeight={() => 'auto'}
						columnVisibilityModel={columnVisibilityModel}
						rows={rows}
						columns={newCustomCol.length > 2 ? newCustomCol : []}
						disableColumnSelector
						filterMode='server'
						sortingMode='server'
						rowCount={Number(totalRowCount) || rows?.length}
						initialState={{
							pagination: { paginationModel: { pageSize: 10 } },
							filter: {
								filterModel: {
									items: [
										{
											field: filterSearch?.field,
											value: filterSearch,
											operator: '='
										}
									]
								}
							}
						}}
						filterModel={{
							items: filterSearch.field
								? [
									{
										field: filterSearch.field,
										operator: filterSearch?.operator || 'contains',
										value: filterSearch.value
									}
								]
								: []
						}}
						localeText={{
							noResultsOverlayLabel: ''
						}}
						pageSizeOptions={[10, 25, 50]}
						slots={{
							pagination: showLoader ? null : TablePaginationDemo,
							noRowsOverlay: CustomNoRowsOverlay,
							columnSortedAscendingIcon: TableFilterIcon,
							columnSortedDescendingIcon: TableFilterIcon
						}}
						slotProps={{
							filterPanel: {
								filterFormProps: {
									operatorInputProps: {
										sx: { display: 'none' }
									}
								}
							},
							pagination: {
								handleChangeRowsPerPage,
								count: rows.length === 0 ? 0 : Number(totalRowCount) || 10,
								rowsPerPage: paginationModel.pageSize,
								page: rows.length === 0 ? 0 : paginationModel.page,
								handleChangePage: handlePaginationModelChange
							}
						}}
						loading={showLoader}
						paginationModel={paginationModel}
						paginationMode={rowsPerPageRender ? 'server' : 'client'}
						onPaginationModelChange={handlePaginationModelChange}
						onFilterModelChange={onFilterChange}
						onSortModelChange={handleSortModelChange}
					/>
				</ThemeProvider>
			)}
		</Box>
	);
};

function TablePaginationDemo({
	handleChangeRowsPerPage,
	handleChangePage,
	page,
	rowsPerPage,
	count
}) {
	return (
		<TablePagination
			component='div'
			count={count}
			page={page}
			onPageChange={handleChangePage}
			rowsPerPage={rowsPerPage}
			onRowsPerPageChange={handleChangeRowsPerPage}
			rowsPerPageOptions={[10, 25, 50]}
		/>
	);
}

export default Tables;

TablePaginationDemo.propTypes = {
	handleChangeRowsPerPage: PropTypes.any,
	handleChangePage: PropTypes.any,
	page: PropTypes.any,
	rowsPerPage: PropTypes.any,
	count: PropTypes.any
};

Tables.propTypes = {
	branchTab: PropTypes.any,
	rows: PropTypes.any,
	columns: PropTypes.any,
	tabValue: PropTypes.any,
	newMobileNumberMap: PropTypes.any,
	hasDuplicates: PropTypes.any,
	totalRowCount: PropTypes.any,
	updatePageCount: PropTypes.any,
	showLoader: PropTypes.any,
	rowsPerPageRender: PropTypes.any,
	onFilterChange: PropTypes.any,
	marginX: PropTypes.any,
	pageCount: PropTypes.any,
	pageSize: PropTypes.any,
	columnVisibilityModel: PropTypes.any,
	kebabMenuItem: PropTypes.any,
	showCustomCol: PropTypes.any,
	tableStatus: PropTypes.any,
	handleSortModelChange: PropTypes.any,
	filterSearch: PropTypes.any,
	apiRef: PropTypes.any,
	showInfo: PropTypes.any,
	handleComment: PropTypes.any,
	isDashboard: PropTypes.bool
};

function InputComponent({ item, applyValue }) {
	const handleKeyDown = (event) => {
		if (event.key !== 'Tab' && event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') {
			event.preventDefault();
		}
	};

	const handleClick = (event) => {
		event.preventDefault();
		event.target.blur();
		event.target.showPicker();
	};

	const handleMouseDown = (event) => {
		event.preventDefault();
	};

	return (
		<TextField
			type='date'
			value={item.value || ''}
			onChange={(event) => applyValue({ ...item, value: event.target.value })}
			onKeyDown={handleKeyDown}
			onClick={handleClick}
			onMouseDown={handleMouseDown}
			sx={{
				'& .MuiOutlinedInput-root': {
					borderTop: 'none',
					borderLeft: 'none',
					borderRight: 'none',
					borderBottom: '1px solid #949494',
					borderRadius: 0,
					height: '48px'
				},
				'& .MuiOutlinedInput-notchedOutline': {
					border: 'none'
				},
				'& .MuiOutlinedInput-input': {
					cursor: 'pointer',
					userSelect: 'none',
					'&:focus': {
						outline: 'none'
					}
				}
			}}
			slotProps={{
				htmlInput: {
					max: todayString
				}
			}}
		/>
	);
}

InputComponent.propTypes = {
	item: PropTypes.any,
	applyValue: PropTypes.any
};
